import type { JourneyExplanation } from '@/api/customer-journey';

/** Form model + (de)serialization for the edit-deal-info drawer. */

export interface DealForm {
  service_interestings: string[];
  budget_explaination: string;
  expected_date: string;
  influencer_explaination: string;
  provider_explaination: string;
  problem_explaination: string;
  expected_explaination: string;
}

/**
 * Serialize a local `YYYY-MM-DD` date-input value to the full ISO datetime the
 * backend expects for `expected_date` (matching legacy `formatPGDate(..,
 * 'YYYY-MM-DDTHH:mm:ssZ')`). Empty input → null.
 */
export function toExpectedDate(dateInput: string): string | null {
  if (!dateInput) return null;
  const d = new Date(`${dateInput}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Local `YYYY-MM-DD` for a date input value (no toISOString / UTC drift). */
export function toDateInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function seedDealForm(ex: JourneyExplanation | null | undefined): DealForm {
  return {
    service_interestings: ex?.service_interestings ?? [],
    budget_explaination: ex?.budget_explaination != null ? String(ex.budget_explaination) : '',
    expected_date: toDateInput(ex?.expected_date),
    influencer_explaination: ex?.influencer_explaination ?? '',
    provider_explaination: ex?.provider_explaination ?? '',
    problem_explaination: ex?.problem_explaination ?? '',
    expected_explaination: ex?.expected_explaination ?? '',
  };
}

/**
 * Build the PUT payload: merge the existing explanation with the edited fields
 * so untouched embedded keys survive (legacy spreads the whole explanation then
 * overrides). Budget is a numeric value (legacy `parseFloat(..) || 0`);
 * `expected_date` is the full ISO datetime or null.
 */
export function toDealPayload(
  ex: JourneyExplanation | null | undefined,
  form: DealForm,
): Partial<JourneyExplanation> {
  const budget = form.budget_explaination.trim();
  const parsed = budget ? parseFloat(budget) : 0;
  return {
    ...ex,
    service_interestings: form.service_interestings,
    budget_explaination: Number.isFinite(parsed) ? parsed : 0,
    expected_date: toExpectedDate(form.expected_date),
    influencer_explaination: form.influencer_explaination.trim(),
    provider_explaination: form.provider_explaination.trim(),
    problem_explaination: form.problem_explaination.trim(),
    expected_explaination: form.expected_explaination.trim(),
  };
}
